import { useCallback } from 'react';
import { parseAbsolute, parseZonedDateTime } from '@internationalized/date';
import { useFormContext } from 'react-hook-form';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { MINUTE_IN_MS } from '@/constants/date.constants';

export const useLocationDateTime = () => {
  const { setValue, getValues } = useFormContext<CommonRouteLegSchemaValues>();

  const handleDepartureLocationDateTime = useCallback(
    (timezone: string) => {
      const { departureDateTime, timeDurationInMs, arrivalDateTime, arrivalLocation, isDateTimeLinked } = getValues();
      if (departureDateTime && timezone) {
        const departureTimeUtcString = parseZonedDateTime(departureDateTime).toAbsoluteString();
        const departureTimeStringInLocationTz = parseAbsolute(departureTimeUtcString, timezone);

        setValue('departureDateTimeValue', departureTimeStringInLocationTz);
        setValue('departureDateTime', departureTimeStringInLocationTz.toString());
        setValue('departureLocalTimezone', departureTimeStringInLocationTz.timeZone);

        if (timeDurationInMs && isDateTimeLinked && arrivalDateTime && arrivalLocation.timezone) {
          const dateTimeValue = departureTimeStringInLocationTz
            .add({ milliseconds: timeDurationInMs })
            .toAbsoluteString();

          const dateTimeValueInLocationTz = parseAbsolute(dateTimeValue, arrivalLocation.timezone);

          setValue('arrivalDateTimeValue', dateTimeValueInLocationTz);
          setValue('arrivalDateTime', dateTimeValueInLocationTz.toString());
          setValue('arrivalLocalTimezone', dateTimeValueInLocationTz.timeZone);
        }
      }
    },
    [getValues, setValue],
  );

  const handleArrivalLocationDateTime = useCallback(
    (timezone: string) => {
      const { departureDateTime, timeDurationInMs, arrivalDateTime, isDateTimeLinked } = getValues();
      const defaultMsToAdd = arrivalDateTime ? 0 : MINUTE_IN_MS;
      const msToAdd = isDateTimeLinked ? timeDurationInMs : defaultMsToAdd;
      const dateTimeValue = isDateTimeLinked ? departureDateTime : arrivalDateTime || departureDateTime;

      if (dateTimeValue && timezone) {
        const arrivalTimeUtcString = parseZonedDateTime(dateTimeValue)
          .add({ milliseconds: msToAdd })
          .toAbsoluteString();

        const arrivalTimeStringInLocationTz = parseAbsolute(arrivalTimeUtcString, timezone);

        setValue('arrivalDateTimeValue', arrivalTimeStringInLocationTz);
        setValue('arrivalDateTime', arrivalTimeStringInLocationTz.toString());
        setValue('arrivalLocalTimezone', arrivalTimeStringInLocationTz.timeZone);
      }
    },
    [getValues, setValue],
  );

  return { handleDepartureLocationDateTime, handleArrivalLocationDateTime };
};
